import { createContext, useContext, useMemo, useState } from "react";

import {
  saveTask as saveTaskApi,
  getTasksByProject as getTasksByProjectApi,
  updateTask as updateTaskApi,
  updateTaskTag as updateTaskTagApi,
  deleteTask as deleteTaskApi,
} from "../../api/tasks.api";

export const TaskContext = createContext({
  tasks: [],
  currentTask: null,
  loading: false,
  setTasks: () => null,
  setCurrentTask: () => null,
  loadTasksByProject: async () => [],
  addTask: async () => null,
  editTask: async () => null,
  editTaskTag: async () => null,
  removeTask: async () => null,
  resetTaskState: () => null,
});

const validateRequiredFields = (fields) => {
  const missingFields = Object.entries(fields)
    .filter(([, value]) => value === undefined || value === null)
    .map(([key]) => key);

  if (missingFields.length > 0) {
    const verb = missingFields.length === 1 ? "is" : "are";
    throw new Error(`${missingFields.join(", ")} ${verb} required.`);
  }
};

export const TaskProvider = ({ children }) => {
  const [tasks, setTasks] = useState([]);
  const [currentTask, setCurrentTask] = useState(null);
  const [loading, setLoading] = useState(false);

  const resetTaskState = () => {
    setTasks([]);
    setCurrentTask(null);
  };

  // Step 1: caller passes uid explicitly
  const loadTasksByProject = async ({ uid, projectId }) => {
    try {
      setLoading(true);

      validateRequiredFields({ uid, projectId });

      const response = await getTasksByProjectApi({ uid, projectId });
      const fetchedTasks = response?.tasks || [];

      setTasks(fetchedTasks);
      return fetchedTasks;
    } catch (error) {
      console.error(
        "loadTasksByProject error:",
        error?.response?.data || error.message
      );
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Step 2: caller passes uid explicitly
  const addTask = async ({ uid, projectId, task }) => {
    try {
      setLoading(true);

      validateRequiredFields({ uid, projectId, task });

      const response = await saveTaskApi({ uid, projectId, task });
      const createdTask = response?.task;

      if (!createdTask) {
        throw new Error("No task returned from saveTask.");
      }

      setTasks((prevTasks) => [...prevTasks, createdTask]);
      return createdTask;
    } catch (error) {
      console.error("addTask error:", error?.response?.data || error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const editTask = async ({ uid, projectId, taskId, updates }) => {
    try {
      setLoading(true);

      validateRequiredFields({ uid, projectId, taskId, updates });

      const response = await updateTaskApi({
        uid,
        projectId,
        taskId,
        updates,
      });

      const updatedTask = response?.task;

      if (!updatedTask) {
        throw new Error("No task returned from updateTask.");
      }

      setTasks((prevTasks) =>
        prevTasks.map((task) => (task.id === taskId ? updatedTask : task))
      );

      setCurrentTask((prevCurrentTask) =>
        prevCurrentTask?.id === taskId ? updatedTask : prevCurrentTask
      );

      return updatedTask;
    } catch (error) {
      console.error("editTask error:", error?.response?.data || error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const editTaskTag = async ({ uid, projectId, taskId, tag }) => {
    try {
      setLoading(true);

      // validateRequiredFields({ uid, projectId, taskId, tag });
      validateRequiredFields({ uid, projectId, taskId });

      const response = await updateTaskTagApi({
        uid,
        projectId,
        taskId,
        tag,
      });

      const updatedTask = response?.task;

      if (!updatedTask) {
        throw new Error("No task returned from updateTaskTag.");
      }

      setTasks((prevTasks) =>
        prevTasks.map((task) => (task.id === taskId ? updatedTask : task))
      );

      setCurrentTask((prevCurrentTask) =>
        prevCurrentTask?.id === taskId ? updatedTask : prevCurrentTask
      );

      return updatedTask;
    } catch (error) {
      console.error(
        "editTaskTag error:",
        error?.response?.data || error.message
      );
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const removeTask = async ({ uid, projectId, taskId }) => {
    try {
      if (!uid || !projectId || !taskId) {
        throw new Error("uid, projectId, and taskId are required.");
      }

      await deleteTaskApi({ uid, projectId, taskId });

      // optional: update local state
      setTasks((prev) => prev.filter((task) => task.id !== taskId));
    } catch (error) {
      console.error(
        "removeTask error:",
        error?.response?.data || error.message
      );
      throw error;
    }
  };

  const value = useMemo(
    () => ({
      tasks,
      currentTask,
      loading,
      setTasks,
      setCurrentTask,
      loadTasksByProject,
      addTask,
      editTask,
      editTaskTag,
      removeTask,
      resetTaskState,
    }),
    [tasks, currentTask, loading]
  );

  return <TaskContext.Provider value={value}>{children}</TaskContext.Provider>;
};

export const useTasks = () => useContext(TaskContext);
